import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  IconButton,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge";
import DownloadIcon from "@mui/icons-material/Download";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { Link } from "react-router-dom";
import YouTubeThumbnailDownloaderContent from "../Contents/YtThumbnailDownloadContent";
import { message } from "../Constants/message.js";
import CommentSection from "../Components/CommentSection.js";

const resolutionMap = {
  default: "120×90",
  medium: "320×180",
  high: "480×360",
  standard: "640×480",
  maximum_resolution: "1280×720",
};

const handleImageDownload = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Error downloading image:", err);
  }
};

function YouTubeThumbnailDownloader({ user, login, isLoggedIn }) {
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [thumbnails, setThumbnails] = useState({});
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setVideoUrl(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);
  const handleGenerateThumbnail = async () => {
    setSuccess("");
    setError("");
    setThumbnails({});

    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }

    if (!videoUrl.trim()) {
      setError("Please enter a YouTube video URL.");
      return;
    }

    if (credits.remaining <= 0) {
      setError("Not enough credits to download YouTube thumbnail images.");
      return;
    }

    // ✅ Basic YouTube URL validation
    const youtubeRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/(watch\?v=|embed\/|shorts\/)?([a-zA-Z0-9_-]{11})/;

    if (!youtubeRegex.test(videoUrl)) {
      setError(
        "Please enter a valid YouTube video URL. It should be like https://www.youtube.com/watch?v=VIDEO_ID"
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/thumbnailDownloader`,
        { videoThumInput: videoUrl },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      setThumbnails({
        default: response.data.defaultUrl,
        medium: response.data.mediumUrl,
        high: response.data.highUrl,
        standard: response.data.standardUrl,
        maximum_resolution: response.data.maxresUrl,
      });

      setSuccess(response.data.successMessage);

      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );

      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });
    } catch (err) {
      setError(
        err.response?.data?.errorMessage ||
          "Failed to download thumbnail. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            Download YouTube Thumbnails 🖼️
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Enter a YouTube video URL to fetch its thumbnails
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="YouTube Video URL"
              variant="outlined"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="e.g. https://www.youtube.com/watch?v=abc123"
              size="medium"
              id="youtube-thumbnail-url"
              name="primary-textbox"
            />
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleGenerateThumbnail}
            >
              Get Thumbnails
            </StyledButton>
          </Box>

          {Object.values(thumbnails).some(Boolean) && (
            <Box mt={3}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mb={1}
                align="center"
              >
                Available Thumbnail Resolutions on YouTube for the given video
                URL
              </Typography>
              <Box
                display="flex"
                flexWrap="wrap"
                gap={2}
                justifyContent="center"
              >
                {Object.entries(thumbnails).map(
                  ([key, url]) =>
                    url && (
                      <Box key={key} sx={{ textAlign: "center" }}>
                        <Box position="relative">
                          <Typography variant="body2" mb={1}>
                            {key.toUpperCase()} ({resolutionMap[key] || "N/A"})
                          </Typography>
                          <Box position="relative" display="inline-block">
                            <img
                              src={url}
                              alt={`${key} thumbnail`}
                              style={{ width: 200, borderRadius: 8 }}
                            />
                            <Tooltip
                              title={`Download (${
                                resolutionMap[key] || "N/A"
                              })`}
                            >
                              <IconButton
                                onClick={() =>
                                  handleImageDownload(
                                    url,
                                    `${key}-thumbnail.jpg`
                                  )
                                }
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  backgroundColor: "rgba(255,255,255,0.8)",
                                  "&:hover": { backgroundColor: "white" },
                                }}
                                size="small"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    )
                )}
              </Box>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <b> 💡 1 Video Thumbnail search = 1 Credit</b>
              <br></br>
              Enter the video URL that you copied from YouTube to download
              thumbnail in multiple resolutions.<br></br>
              Having trouble? <Link to="/contact">Contact support</Link>
            </Typography>
          </Box>
        </ContentBox>
        <CommentSection
          toolId="youtube/thumbnailDownloader"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeThumbnailDownloaderContent />
      </PageContainer>
    </>
  );
}

export default YouTubeThumbnailDownloader;
